import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Button,
  Box,
} from "@mui/material";
import { useMediaQuery, useTheme } from "@mui/material";
import Color from "../../components/Color";
import { useSnackbar } from "../../App";

const AddBranch = ({ open, onClose, formData, setFormData, onSubmit, editingId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showSnackbar } = useSnackbar();
  const [errors, setErrors] = useState({});
  const codeInputRef = useRef(null); // Reference to focus the code field on error

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = ["Branch name is required"];
    } else if (!/^[a-zA-Z ]+$/.test(formData.name)) {
      newErrors.name = ["Branch name must contain only letters"];
    }

    if (!formData.code.trim()) {
      newErrors.code = ["Branch code is required"];
    }

    if (!formData.address.trim()) {
      newErrors.address = ["Address is required"];
    }

    if (!formData.phone.trim()) {
      newErrors.phone = ["Phone is required"];
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = ["Phone must be exactly 10 digits"];
    } else if (
      /^(\d)\1{9}$/.test(formData.phone) || // All digits same
      /^[01]/.test(formData.phone) // Starts with 0 or 1
    ) {
      newErrors.phone = ["Enter a valid mobile number"];
    }

    if (!formData.email.trim()) {
      newErrors.email = ["Email is required"];
    } else if (
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i.test(formData.email)
    ) {
      newErrors.email = ["Invalid email format"];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      showSnackbar("Please fix validation errors before submitting.", "error");
      return;
    }

    try {
      await onSubmit(setErrors); // Pass setErrors as errorsCallback
      // Success handling is done in Branches
    } catch (err) {
      if (err.response?.data?.errors?.code) {
        codeInputRef.current?.focus(); // Focus the code field on error
        setErrors((prev) => ({
          ...prev,
          code: err.response.data.errors.code,
        }));
      }
    }
  };

  const handleCancel = () => {
    setFormData({
      name: "",
      code: "",
      address: "",
      phone: "",
      email: "",
      is_active: true,
    });
    setErrors({});
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "email") {
      const lowerCaseValue = value.toLowerCase().replace(/[^a-z0-9@.]/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: lowerCaseValue,
      }));
      if (lowerCaseValue && !/^[a-z0-9._]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(lowerCaseValue)) {
        setErrors((prev) => ({ ...prev, email: ["Invalid email format"] }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.email;
          return newErrors;
        });
      }
      return;
    }

    if (name === "name") {
      const isValid = /^[a-zA-Z ]*$/.test(value);
      const filtered = value.replace(/[^a-zA-Z ]/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: filtered,
      }));
      if (!isValid) {
        setErrors((prev) => ({
          ...prev,
          name: ["Only letters are allowed"],
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.name;
          return newErrors;
        });
      }
    } else if (name === "phone") {
      const filteredValue = value.replace(/[^0-9]/g, "");
      setFormData((prev) => ({
        ...prev,
        phone: filteredValue,
      }));

      if (/[^0-9]/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          phone: ["Only numbers are allowed"],
        }));
      } else if (filteredValue.length === 10) {
        if (/^(\d)\1{9}$/.test(filteredValue) || /^[01]/.test(filteredValue)) {
          setErrors((prev) => ({
            ...prev,
            phone: ["Enter a valid mobile number"],
          }));
        } else {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.phone;
            return newErrors;
          });
        }
      } else if (filteredValue.length > 0) {
        setErrors((prev) => ({
          ...prev,
          phone: ["Phone must be exactly 10 digits"],
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          phone: ["Phone is required"],
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSwitchChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      is_active: e.target.checked,
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleCancel();
        } else if (Object.keys(errors).length > 0) {
          showSnackbar("Please fix validation errors before closing.", "error");
          return; // Prevent closing if there are errors
        }
      }}
      disableEscapeKeyDown={Object.keys(errors).length > 0}
      PaperProps={{
        sx: {
          width: isMobile ? "90vw" : "400px",
          maxWidth: "95vw",
          borderRadius: "8px",
          minHeight: isMobile ? "auto" : "660px",
        },
      }}
    >
      <DialogTitle sx={{ fontSize: isMobile ? "1rem" : "1.25rem" }}>
        {editingId ? "Edit Branch" : "Add Branch"}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          px: isMobile ? 2 : 3,
          py: isMobile ? 1 : 2,
          maxHeight: isMobile ? "70vh" : "auto",
          overflowY: "auto",
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* Branch Name */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Branch Name*
              </Typography>
              <TextField
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter branch name"
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.[0]}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                }}
              />
            </Box>

            {/* Branch Code */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Branch Code*
              </Typography>
              <TextField
                name="code"
                value={formData.code}
                onChange={handleChange}
                placeholder="Enter branch code"
                fullWidth
                error={!!errors.code}
                helperText={errors.code?.[0]}
                inputProps={{ maxLength: 10 }}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                inputRef={codeInputRef}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                    borderColor: "error.main",
                  },
                }}
              />
            </Box>

            {/* Phone */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Phone*
              </Typography>
              <TextField
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter phone number"
                fullWidth
                error={!!errors.phone}
                helperText={errors.phone?.[0]}
                inputProps={{ maxLength: 10, inputMode: "numeric", pattern: "[0-9]*" }}
                type="tel"
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                }}
              />
            </Box>

            {/* Email */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Email*
              </Typography>
              <TextField
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address"
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.[0]}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                }}
              />
            </Box>

            {/* Address */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Address*
              </Typography>
              <TextField
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter address"
                fullWidth
                error={!!errors.address}
                helperText={errors.address?.[0]}
                multiline
                rows={4}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                  "& .MuiInputBase-inputMultiline": {
                    paddingTop: "8px",
                    paddingBottom: "8px",
                    lineHeight: 1.5,
                  },
                }}
              />
            </Box>

            {/* Active/Inactive Switch */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Status
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleSwitchChange}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: Color.primary.main,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: Color.primary.main,
                      },
                      "& .MuiSwitch-track": {
                        backgroundColor: "#ccc",
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      color: formData.is_active ? Color.primary.main : "text.secondary",
                      fontWeight: formData.is_active ? "bold" : "normal",
                      fontSize: isMobile ? "0.85rem" : "1rem",
                    }}
                  >
                    {formData.is_active ? "Active" : "Inactive"}
                  </Typography>
                }
              />
            </Box>

            {/* Backend Error Message */}
            {errors.form && (
              <Typography color="error" variant="body2" mt={1}>
                {errors.form}
              </Typography>
            )}
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={2} mt={2} mb={1}>
            <Button
              onClick={handleCancel}
              sx={{
                background: "none",
                color: Color.secondary.main,
                border: "1px solid",
                borderColor: Color.secondary.main,
                width: isMobile ? "100%" : "auto",
                fontSize: isMobile ? "0.85rem" : "1rem",
                padding: isMobile ? "8px" : "6px 16px",
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              sx={{
                background: Color.primary.main,
                color: "white",
                width: isMobile ? "100%" : "auto",
                fontSize: isMobile ? "0.85rem" : "1rem",
                padding: isMobile ? "8px" : "6px 16px",
                "&:hover": {
                  background: Color.primary.dark,
                },
              }}
            >
              {editingId ? "Update" : "Submit"}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddBranch;